import type { WordCardKind } from "@-/dev.improve.haniverse.database.schemas.actor.schema";

import type { CardContent } from "#card-content";
import { wordCardContent } from "#word-card-kind-mapping";

/**
 * The content-side app-card row shape this resolver reads. Structurally matches
 * `UserCardAppCard` from `@-/dev.improve.haniverse.lib.cards.app-card`, but is declared
 * structurally here so this package doesn't depend on that one (the dependency runs the
 * other way — the app-card loader is a caller, not a provider, of card-content semantics).
 */
export type AppCardOverlays = {
  appGraphemeMeaningCard: { grapheme: string } | null;
  appGraphemePronunciationCard: { grapheme: string; pinyin: string } | null;
  /**
   * The ACTOR-side quiz overlay. Like a word card, a quiz has no content-catalog row: its content
   * ships in the app bundle (the quiz manifest), found by this key.
   */
  userQuizCard?: { key: string } | null;
  /**
   * The ACTOR-side word overlay, not a content-catalog row.
   *
   * A word card deliberately has no `app_*_card` row: its prompt is assembled at review time
   * from `sentence` + `sentence_word`, so there is nothing to put in the catalog. Resolving it
   * therefore needs the actor row, which is why this one field breaks the "app card" symmetry.
   * Without it every word card resolves to `null` here and is silently skipped by the scheduler
   * and the study list — the card exists, is due, and never appears.
   *
   * `kind` is REQUIRED: it is what distinguishes the three word cards a single `(word, pinyin)`
   * can have. Any caller that projects columns for this overlay must select it, or every word card
   * resolves to one kind and is scheduled under the wrong review-eligibility gate.
   */
  userWordSentenceCard?: { kind: WordCardKind; pinyin: string; word: string } | null;
};

/**
 * Resolve which content card a `user_card`'s app-card overlays represent.
 *
 * Replaces the `appGraphemePronunciationCard ?? appGraphemeMeaningCard` idiom that was
 * duplicated verbatim across the next-due scheduler, the study-card list, and the
 * learn-summary navigator. That `??` is only correct while there are exactly two kinds: a
 * *third* kind of card would resolve to whichever of the two happened to be non-null — i.e.
 * to the wrong card — with no error. Routing every caller through one function means adding a
 * kind is an edit here rather than a hunt for three copies of an idiom.
 *
 * Precedence is pronunciation-over-meaning, preserving the `??` exactly. Note the overlay
 * tables each carry a `userCardId` unique index, but that is *per table* — it forbids two
 * meaning overlays on one card, not one meaning plus one pronunciation. "Both present" is
 * therefore prevented by `createUserCard` (each overlay is inserted against its own fresh
 * `user_card`) rather than by the schema, so this deliberately does NOT throw on it: that
 * would turn malformed rows from a silently-mis-rated card into a crashed review queue, which
 * is a behaviour change this refactor is not entitled to make. See the ambiguity note in the
 * package README for why the constraint belongs in the schema instead.
 *
 * Returns `null` when no overlay is present — a `user_card` whose content row is missing (a
 * soft-deleted or not-yet-synced overlay), which every caller already skips.
 */
export function resolveAppCardContent(overlays: AppCardOverlays): CardContent | null {
  const { appGraphemeMeaningCard, appGraphemePronunciationCard, userQuizCard, userWordSentenceCard } =
    overlays;

  if (userQuizCard !== null && userQuizCard !== undefined) {
    return { key: userQuizCard.key, kind: "quiz" };
  }

  // Checked first: a word card carries no app-card overlays at all, so the grapheme branches
  // below would both miss and return null.
  if (userWordSentenceCard !== null && userWordSentenceCard !== undefined) {
    return wordCardContent({
      kind: userWordSentenceCard.kind,
      pinyin: userWordSentenceCard.pinyin,
      word: userWordSentenceCard.word,
    });
  }

  if (appGraphemePronunciationCard !== null) {
    return {
      grapheme: appGraphemePronunciationCard.grapheme,
      kind: "grapheme-pronunciation",
      pinyin: appGraphemePronunciationCard.pinyin,
    };
  }

  if (appGraphemeMeaningCard !== null) {
    return { grapheme: appGraphemeMeaningCard.grapheme, kind: "grapheme-meaning" };
  }

  return null;
}
